/**
 * ENG-5688 — feature flag for the `agt impersonate` family of commands.
 *
 * The flow is still v0 and there are pieces (channel-MCP refusal under
 * acting-as JWT, slash-command wrappers) that haven't landed yet, so we
 * gate the commands behind an env var to keep them out of general
 * operators' hands until the surrounding pieces are in. Once those land
 * (ENG-5689 + ENG-5690) and the spike's smoke test has been run end-to-end
 * against production data, the flag can be removed and the commands
 * promoted to defaults.
 *
 * Set `AGT_IMPERSONATE_ENABLED=1` (or any truthy value) to enable.
 */

const ENV_VAR = 'AGT_IMPERSONATE_ENABLED';

/**
 * Returns true if the operator has explicitly opted in to the
 * impersonation commands. Used by every `agt impersonate *` subcommand
 * to gate access.
 */
export function impersonateEnabled(): boolean {
  const v = process.env[ENV_VAR];
  if (!v) return false;
  const trimmed = v.trim().toLowerCase();
  // Treat the obvious "off" values as disabled to avoid silent footguns
  // (`AGT_IMPERSONATE_ENABLED=0` shouldn't be truthy just because the
  // string is non-empty).
  return !(trimmed === '' || trimmed === '0' || trimmed === 'false' || trimmed === 'no');
}

/**
 * Bail with a helpful message when the flag is not set. Returns void on
 * success; calls `process.exit(2)` otherwise so the command terminates
 * before any state-mutating work runs.
 *
 * Exit code 2 (rather than 1) so callers / CI scripts can distinguish
 * "feature gated off" from "command failed for some other reason".
 */
export function requireImpersonateEnabled(): void {
  if (impersonateEnabled()) return;

  process.stderr.write(
    `\nThe \`agt impersonate\` commands are gated behind a feature flag.\n` +
      `\n` +
      `To enable them, set ${ENV_VAR}=1 in your environment:\n` +
      `\n` +
      `  export ${ENV_VAR}=1\n` +
      `\n` +
      `These commands are part of the operator-impersonates-agent v0 flow\n` +
      `(ENG-5687 / ENG-5688). The gating will be removed once the\n` +
      `surrounding pieces (ENG-5689 channel-MCP refusal, ENG-5690 slash\n` +
      `commands) land and the end-to-end smoke test has been run against\n` +
      `production.\n` +
      `\n`,
  );
  process.exit(2);
}

// Exported for tests so they don't have to hard-code the variable name.
export const IMPERSONATE_FLAG_ENV_VAR = ENV_VAR;
